import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr, type LedgerAccount } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function AccountingGeneralLedgerPage() {
  const { hospitalId } = useAccountingHospitalContext();
  const [ledgerAccountId, setLedgerAccountId] = useState("");
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [runKey, setRunKey] = useState(0);

  const { data: accounts = [] } = useQuery({
    queryKey: ["accounting-accounts", hospitalId],
    queryFn: () => accountingApi.listLedgerAccounts(hospitalId),
    enabled: !!hospitalId,
  });

  const { data: gl, isFetching } = useQuery({
    queryKey: ["accounting-gl", hospitalId, ledgerAccountId, fromDate, toDate, runKey],
    queryFn: () =>
      accountingApi.generalLedger(hospitalId, {
        ledgerAccountId,
        fromDate,
        toDate,
      }),
    enabled: !!hospitalId && !!ledgerAccountId && runKey > 0,
  });

  const report = gl as {
    account?: { code: string; name: string };
    opening?: { debit: number; credit: number; net: number };
    entries?: Array<{
      lineId: string;
      voucherId: string;
      voucherNumber: string;
      voucherDate: string;
      narration?: string | null;
      debit: number;
      credit: number;
      runningBalance: number;
    }>;
    closing?: { debit: number; credit: number; net: number };
  } | undefined;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-medium">General ledger</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72 space-y-1">
          <Label className="text-xs">Account</Label>
          <Select value={ledgerAccountId || undefined} onValueChange={setLedgerAccountId}>
            <SelectTrigger>
              <SelectValue placeholder="Select account" />
            </SelectTrigger>
            <SelectContent>
              {(accounts as LedgerAccount[]).map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <Button
          disabled={!ledgerAccountId}
          onClick={() => setRunKey((k) => k + 1)}
        >
          Run
        </Button>
      </div>

      {isFetching ? <p className="text-sm text-slate-500">Loading…</p> : null}

      {report?.account ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {report.account.code} — {report.account.name}
          </p>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Voucher</TableHead>
                  <TableHead>Narration</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={3} className="font-medium">
                    Opening
                  </TableCell>
                  <TableCell className="text-right">
                    {report.opening?.debit ? formatInr(report.opening.debit) : ""}
                  </TableCell>
                  <TableCell className="text-right">
                    {report.opening?.credit ? formatInr(report.opening.credit) : ""}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatInr(report.opening?.net ?? 0)}
                  </TableCell>
                </TableRow>
                {(report.entries || []).map((e) => (
                  <TableRow key={e.lineId}>
                    <TableCell>{String(e.voucherDate).slice(0, 10)}</TableCell>
                    <TableCell>
                      <Link
                        className="underline-offset-2 hover:underline"
                        to={`../vouchers/${e.voucherId}`}
                      >
                        {e.voucherNumber}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate">
                      {e.narration || ""}
                    </TableCell>
                    <TableCell className="text-right">
                      {e.debit ? formatInr(e.debit) : ""}
                    </TableCell>
                    <TableCell className="text-right">
                      {e.credit ? formatInr(e.credit) : ""}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatInr(e.runningBalance)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} className="font-semibold">
                    Closing
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {report.closing?.debit ? formatInr(report.closing.debit) : ""}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {report.closing?.credit ? formatInr(report.closing.credit) : ""}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatInr(report.closing?.net ?? 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
